import { CheckboxBase, Divider, Skeleton, Typography } from 'omakase';
import {
  DEFAULT_FAVORITES_PLAYLIST,
  DEFAULT_STARRED_PLAYLIST,
  DEFAULT_WANT_TO_GO_PLAYLIST,
  PLAYLIST_DEFAULTS,
} from 'selectors/patronSelectors';
import { Grid } from 'ui';
import { Grow } from '@material-ui/core';
import { PlaylistType, getPlaylistType } from 'utils/profileUtils';
import { Theme, makeStyles } from '@material-ui/core/styles';
import { consumer, core, payloadToJSON, safeToNumber } from '@tock/coretypes';
import { useActions } from '@tock/shared/utils/connect';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePrevious } from '@tock/hooks';
import PlaylistTypeIcon from 'components/profile/playlists/PlaylistTypeIcon';
import _ from 'lodash';
import clsx from 'clsx';

export interface PlaylistDescriptor {
  name: string;
  id: number;
  playlistType: PlaylistType;
  numItems: number;
  isPublic: boolean;
}

/**
 *
 * Processes the playlists into a simpler PlaylistDescriptor object,
 * inserts defaults if the user hasn't created them yet,
 * and sorts the defaults in a specific order to the top
 *
 * @param playlists
 * @returns
 */
export const processPlaylistsToPlaylistDescriptors = (
  playlists: consumer.Playlist[]
): PlaylistDescriptor[] => {
  const hasStarred =
    _.findIndex(playlists, (playlist) => playlist.name === DEFAULT_STARRED_PLAYLIST) !== -1;
  const hasWantToGo =
    _.findIndex(playlists, (playlist) => playlist.name === DEFAULT_WANT_TO_GO_PLAYLIST) !== -1;

  const hasFavorites =
    _.findIndex(playlists, (playlist) => playlist.name === DEFAULT_FAVORITES_PLAYLIST) !== -1;

  let playlistsData: PlaylistDescriptor[] = (playlists || [])
    .filter((playlist) => !playlist.isArchived)
    .map((playlist) => ({
      id: playlist.id,
      name: playlist.name,
      numItems: playlist.items.length,
      playlistType: getPlaylistType(playlist),
      isPublic: playlist.isPublic,
    }));

  if (!hasWantToGo) {
    playlistsData = [
      {
        id: -2,
        name: DEFAULT_WANT_TO_GO_PLAYLIST,
        playlistType: 'WANT_TO_GO',
        numItems: 0,
        isPublic: false,
      },
      ...playlistsData,
    ];
  }

  if (!hasFavorites) {
    playlistsData = [
      {
        id: -3,
        name: DEFAULT_FAVORITES_PLAYLIST,
        playlistType: 'FAVORITE',
        numItems: 0,
        isPublic: false,
      },
      ...playlistsData,
    ];
  }

  if (!hasStarred) {
    playlistsData = [
      {
        id: -1,
        name: DEFAULT_STARRED_PLAYLIST,
        playlistType: 'STAR',
        numItems: 0,
        isPublic: false,
      },
      ...playlistsData,
    ];
  }

  return playlistsData.sort(sortPlaylists);
};

const defaultIdToPlaylist: Dictionary<string> = {
  '-3': DEFAULT_FAVORITES_PLAYLIST,
  '-2': DEFAULT_WANT_TO_GO_PLAYLIST,
  '-1': DEFAULT_STARRED_PLAYLIST,
};

/**
 * Sort playlists so "Starred" is at the top, then "Favorites", then "Want to go", then alphabetically
 * @param playlistA
 * @param playlistB
 * @returns sorted playlists
 */
const sortPlaylists = (playlistA: PlaylistDescriptor, playlistB: PlaylistDescriptor) => {
  const playlistAIsDefault = PLAYLIST_DEFAULTS.includes(playlistA.name);
  const playlistBIsDefault = PLAYLIST_DEFAULTS.includes(playlistB.name);
  if (playlistAIsDefault || playlistBIsDefault) {
    if (playlistAIsDefault && playlistBIsDefault) {
      if (playlistA.name === DEFAULT_STARRED_PLAYLIST) {
        return -1;
      } else if (playlistB.name === DEFAULT_STARRED_PLAYLIST) {
        return 1;
      } else if (playlistA.name === DEFAULT_FAVORITES_PLAYLIST) {
        return -1;
      } else if (playlistB.name === DEFAULT_FAVORITES_PLAYLIST) {
        return 1;
      } else if (playlistA.name === DEFAULT_WANT_TO_GO_PLAYLIST) {
        return -1;
      } else {
        return 1;
      }
    } else {
      return playlistAIsDefault ? -1 : 1;
    }
  }
  return playlistA.name > playlistB.name ? 1 : -1;
};

interface Props {
  playlists: consumer.Playlist[];
  business: core.IBusiness;
  onClickNote(playlist: consumer.Playlist, playlistItem: consumer.IPlaylistItem): void;
  isOpen: boolean;
}

const BusinessPlaylistsDialogListsPage: React.FunctionComponent<Props> = ({
  playlists,
  business,
  onClickNote,
  isOpen,
}) => {
  const { actions } = useActions();
  const [isLoading, setIsLoading] = useState(false);

  const playlistsWithBusiness = useMemo(
    () =>
      playlists?.filter(
        (playlist) => playlist.items.findIndex((item) => item.business?.id === business.id) !== -1
      ) || [],
    [business.id, playlists]
  );

  const idToPlaylistWithBusiness: Dictionary<consumer.Playlist> = _.keyBy<consumer.Playlist>(
    playlistsWithBusiness,
    (playlist) => playlist.id
  );
  const playlistIdToBusinessItem = _.mapValues(idToPlaylistWithBusiness, (playlist) =>
    playlist.items.find((item) => item.business?.id === business.id)
  );

  const playlistsWithDefaults = processPlaylistsToPlaylistDescriptors(playlists || []);

  const getOnChangeSelected = useCallback(
    (playlistId: number) => {
      return (isSelected: boolean) => {
        if (isSelected) {
          window.analytics.sendEvent('Playlist', 'Business added', business.name);
          if (playlistId < 0) {
            const name = defaultIdToPlaylist[_.toString(playlistId)];
            return actions.patron.upsertPlaylist({ name }).then((response) => {
              const returnedPlaylist = payloadToJSON<consumer.Playlist>(response.payload);
              actions.patron.upsertPlaylistItem(safeToNumber(returnedPlaylist.id), {
                businessId: business.id,
              });
            });
          } else {
            return actions.patron.upsertPlaylistItem(playlistId, {
              businessId: business.id,
            });
          }
        } else {
          window.analytics.sendEvent('Playlist', 'Business removed', business.name);
          const playlistItemId = playlistIdToBusinessItem[playlistId]?.id;
          return playlistItemId
            ? actions.patron.deletePlaylistItem(playlistId, playlistItemId)
            : Promise.resolve();
        }
      };
    },
    [actions.patron, business.id, business.name, playlistIdToBusinessItem]
  );

  const prevIsOpen = usePrevious(isOpen);

  useEffect(() => {
    if (isOpen && !prevIsOpen && _.isEmpty(playlistsWithBusiness)) {
      const starredPlaylist = playlistsWithDefaults.find(
        (playlist) => playlist.playlistType === 'STAR'
      );
      if (starredPlaylist) {
        setIsLoading(true);
        getOnChangeSelected(starredPlaylist.id)(true)
          .then(() => actions.toast.showInfo(`${business.name} Saved`))
          .finally(() => setIsLoading(false));
      }
    }
  }, [
    isOpen,
    prevIsOpen,
    playlistsWithBusiness,
    playlistsWithDefaults,
    getOnChangeSelected,
    actions.toast,
    business.name,
  ]);

  if (isLoading || !isOpen) {
    return <BusinessPlaylistsDialogListsPageSkeleton />;
  }

  return (
    <>
      {playlistsWithDefaults?.map((playlist) => {
        const isSelected = !!playlistIdToBusinessItem[playlist.id];
        const playlistItem = playlistIdToBusinessItem[playlist.id];
        const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          getOnChangeSelected(playlist.id)(!isSelected);
        };
        const actionComponent = (
          <CheckboxBase
            checked={isSelected}
            value={isSelected}
            onChange={onChange}
            aria-label={`Save to ${playlist.name}`}
          />
        );
        return (
          <Grid container spacing={2} key={`playlist-${playlist.id}`}>
            <Grid item xs={12}>
              <BusinessPlaylistsList
                onClickNote={() =>
                  playlistItem && onClickNote(idToPlaylistWithBusiness[playlist.id], playlistItem)
                }
                playlistItem={playlistItem}
                playlist={playlist}
                actionComponent={actionComponent}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider mb={1} />
            </Grid>
          </Grid>
        );
      })}
    </>
  );
};

export default BusinessPlaylistsDialogListsPage;

interface BusinessPlaylistsListProps {
  playlist: PlaylistDescriptor;
  actionComponent: React.ReactNode;
  onClick?(playlistId: number): void;
  onClickNote?(): void;
  playlistItem?: consumer.IPlaylistItem;
}

const useBusinessPlaylistListStyles = makeStyles((theme) => ({
  listContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  listInfoAndIconContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    textAlign: 'left',
    justifyContent: 'space-between',
  },
  listInfoContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  listInfoNoteSpacing: {
    paddingBottom: theme.spacing(2.5),
  },
  playlistIcon: {
    marginTop: theme.spacing(1.5),
  },
}));

export const BusinessPlaylistsList: React.FunctionComponent<BusinessPlaylistsListProps> = ({
  playlist,
  actionComponent,
  onClick,
  onClickNote,
  playlistItem,
}) => {
  const styles = useBusinessPlaylistListStyles();

  const Wrapper = ({ children }: { children: React.ReactNode }) => {
    return onClick ? (
      <button className={styles.listInfoContainer} onClick={() => onClick(playlist.id)}>
        {children}
      </button>
    ) : (
      <div className={styles.listInfoContainer}>{children}</div>
    );
  };

  return (
    <div className={styles.listContainer}>
      <Grid container>
        <Grid item xs={1}>
          <PlaylistTypeIcon className={styles.playlistIcon} playlistType={playlist.playlistType} />
        </Grid>
        <Grid item xs={11}>
          <Grid container>
            <Grid item xs={12}>
              <div
                className={clsx(styles.listInfoAndIconContainer, {
                  [styles.listInfoNoteSpacing]: !!playlistItem,
                })}
              >
                <Wrapper>
                  <Typography component="h4" variant="body01Strong">
                    {playlist.name}
                  </Typography>
                  <Typography variant="body02" color="fgSubtle">
                    {playlist.isPublic ? 'Public' : 'Private'} • {playlist.numItems}{' '}
                    {playlist.numItems === 1 ? 'place' : 'places'}
                  </Typography>
                </Wrapper>
                <div>{actionComponent}</div>
              </div>
            </Grid>
            <Grid item xs={12}>
              {playlistItem && (
                <Grid item xs={12}>
                  <PlaylistNoteButton
                    onClickNote={onClickNote}
                    note={playlistItem.note || undefined}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

const usePlaylistNoteButtonStyles = makeStyles((theme: Theme) => ({
  noteButton: {
    display: 'flex',
    minHeight: theme.spacing(5),
    height: '100%',
    textAlign: 'start',
    padding: theme.spacing(2),
    border: `1px solid ${theme.colors.borderDefault}`,
    borderRadius: theme.borderRadius[100],
    width: '100%',
  },
  noteButtonClickable: {
    cursor: 'pointer',
  },
}));

export const PlaylistNoteButton: React.FunctionComponent<{
  note: string | undefined;
  onClickNote?(): void;
}> = ({ note, onClickNote }) => {
  const styles = usePlaylistNoteButtonStyles();
  const noteBody = onClickNote ? (
    <button className={clsx(styles.noteButton, styles.noteButtonClickable)} onClick={onClickNote}>
      <Typography color={note ? undefined : 'fgSubtle'}>
        {note || 'Add a note about this place?'}
      </Typography>
    </button>
  ) : (
    <div className={styles.noteButton}>
      <Typography color={note ? undefined : 'fgSubtle'}>
        {note || 'Add a note about this place?'}
      </Typography>
    </div>
  );

  return <Grow in={true}>{noteBody}</Grow>;
};

const useSkeletonStyles = makeStyles((theme) => ({
  skeletonBlock: {
    width: '100%',
  },
}));

const BusinessPlaylistsDialogListsPageSkeleton: React.FunctionComponent = () => {
  const styles = useSkeletonStyles();
  return (
    <Grid container spacing={2}>
      {_.range(0, 4).map((index) => (
        <Grid item xs={12} key={index}>
          <Skeleton className={styles.skeletonBlock} height={50} variant="rect" />
        </Grid>
      ))}
    </Grid>
  );
};
